import type { ComponentBaseProps } from "@/ui/componentBase";
import type React from "react";
import { getBackgroundColorClass } from "@/lib/colors";
import { getStateMap } from "../mapperLookup";
import type {
  ComponentBaseContext,
  ComponentBaseMapper,
  ExecutionDetailsContext,
  NodeInfo,
  SubtitleContext,
} from "../types";
import type { MetadataItem } from "@/ui/metadataList";
import jiraIcon from "@/assets/icons/integrations/jira.svg";
import { renderTimeAgo } from "@/components/TimeAgo";
import { jiraBaseEventSections } from "./base";

interface CreateIncidentConfiguration {
  serviceDesk?: string;
  serviceDeskRequestType?: string;
  summary?: string;
}

interface CreateIncidentNodeMetadata {
  serviceDeskName?: string;
  requestTypeName?: string;
}

export const createIncidentMapper: ComponentBaseMapper = {
  props(context: ComponentBaseContext): ComponentBaseProps {
    const lastExecution = context.lastExecutions.length > 0 ? context.lastExecutions[0] : null;
    const componentName = context.componentDefinition.name ?? "jira.createIncident";

    return {
      iconSrc: jiraIcon,
      collapsedBackground: getBackgroundColorClass(context.componentDefinition.color),
      collapsed: context.node.isCollapsed,
      title: context.node.name || context.componentDefinition.label || "Unnamed component",
      eventSections: lastExecution ? jiraBaseEventSections(context.nodes, lastExecution, componentName) : undefined,
      metadata: metadataList(context.node),
      includeEmptyState: !lastExecution,
      eventStateMap: getStateMap(componentName),
    };
  },

  getExecutionDetails(context: ExecutionDetailsContext): Record<string, string> {
    const details: Record<string, string> = {};
    if (context.execution.createdAt) {
      details["Executed At"] = new Date(context.execution.createdAt).toLocaleString();
    }
    const outputs = context.execution.outputs as { default?: Array<{ data?: unknown }> } | undefined;
    const data = outputs?.default?.[0]?.data as Record<string, unknown> | undefined;
    if (!data) {
      return details;
    }
    if (data.key != null) {
      details["Issue key"] = String(data.key);
    }
    if (data.self != null) {
      details["url"] = String(data.self);
    }
    return details;
  },

  subtitle(context: SubtitleContext): string | React.ReactNode {
    if (!context.execution.createdAt) return "";
    return renderTimeAgo(new Date(context.execution.createdAt));
  },
};

function metadataList(node: NodeInfo): MetadataItem[] {
  const configuration = node.configuration as CreateIncidentConfiguration;
  const nodeMetadata = node.metadata as CreateIncidentNodeMetadata | undefined;

  const items: MetadataItem[] = [];

  const deskLabel = nodeMetadata?.serviceDeskName || configuration?.serviceDesk;
  if (deskLabel) {
    items.push({ icon: "layers", label: deskLabel });
  }

  const typeLabel = nodeMetadata?.requestTypeName || configuration?.serviceDeskRequestType;
  if (typeLabel) {
    items.push({ icon: "tag", label: typeLabel });
  }

  if (configuration?.summary) {
    items.push({ icon: "file-text", label: configuration.summary });
  }

  return items.slice(0, 3);
}
